// Only the static selector projection is persisted, never business data or grants.
const FRESH_FOR = 24 * 60 * 60 * 1000;
const KEEP_FOR = 30 * FRESH_FOR;
const RETRY_AFTER = 60 * 1000;
const MAX_ENTRIES = 150;
const MAX_CHARACTERS = 500000;
const FIELDS = {
  brands: "brand",
  models: "model",
  years: "year",
  versions: "version",
};

const isAuthorizationError = (error) =>
  /^(functions\/)?(unauthenticated|permission-denied)$/.test(error?.code || "");

function projectCatalogue(kind, response) {
  const rows = Array.isArray(response)
    ? response
    : response?.success === "yes"
      ? response.data
      : null;
  if (!Array.isArray(rows)) throw new Error("Respuesta de catálogo inválida");
  const field = FIELDS[kind];
  return rows.map((row) => {
    if (
      !row ||
      typeof row.id !== "string" ||
      !row.id ||
      !(
        typeof row[field] === "string" ||
        (typeof row[field] === "number" && Number.isFinite(row[field]))
      )
    ) {
      throw new Error("Entrada de catálogo inválida");
    }
    const item = { id: row.id, [field]: row[field] };
    if (typeof row.name === "string") item.name = row.name;
    return item;
  });
}

function createCatalogueCache({
  storage,
  storageKey,
  getScope,
  now = Date.now,
  onError = () => {},
}) {
  let scope;
  let generation = 0;
  let hydration;
  let writes = Promise.resolve();
  const entries = new Map();
  const requests = new Map();
  const retries = new Map();
  const copy = (rows) => rows.map((row) => ({ ...row }));
  const keyFor = (value) => `${storageKey}:${encodeURIComponent(value)}`;

  function reset() {
    generation += 1;
    entries.clear();
    requests.clear();
    retries.clear();
    hydration = undefined;
  }

  function context() {
    const nextScope = getScope();
    if (!nextScope) {
      reset();
      const error = new Error("El usuario no está autenticado");
      error.code = "functions/unauthenticated";
      throw error;
    }
    if (nextScope !== scope) {
      reset();
      scope = nextScope;
    }
    return { scope, generation };
  }

  function assertCurrent(ctx) {
    if (ctx.generation !== generation || ctx.scope !== getScope()) {
      const error = new Error("La sesión cambió durante la carga del catálogo");
      error.code = "functions/unauthenticated";
      throw error;
    }
  }

  function trim() {
    const sorted = [...entries.entries()].sort(
      (a, b) => b[1].savedAt - a[1].savedAt,
    );
    entries.clear();
    let size = 0;
    for (const [key, entry] of sorted) {
      const age = now() - entry.savedAt;
      const length = JSON.stringify([key, entry]).length;
      if (
        age < 0 ||
        age >= KEEP_FOR ||
        entries.size >= MAX_ENTRIES ||
        size + length > MAX_CHARACTERS
      )
        continue;
      size += length;
      entries.set(key, entry);
    }
  }

  async function hydrate(ctx) {
    if (!hydration) {
      hydration = (async () => {
        try {
          // Wait for pending invalidations before reading the same namespace again.
          await writes;
          const raw = await storage.getItem(keyFor(ctx.scope));
          assertCurrent(ctx);
          if (!raw || raw.length > MAX_CHARACTERS + 20000) return;
          const snapshot = JSON.parse(raw);
          if (snapshot.schema !== 1 || !Array.isArray(snapshot.entries)) return;
          for (const [key, entry] of snapshot.entries.slice(0, MAX_ENTRIES)) {
            const [kind] = JSON.parse(key);
            const age = now() - entry.savedAt;
            if (
              !FIELDS[kind] ||
              !Number.isFinite(entry.savedAt) ||
              age < 0 ||
              age >= KEEP_FOR
            )
              continue;
            entries.set(key, {
              savedAt: entry.savedAt,
              data: projectCatalogue(kind, entry.data),
            });
          }
          trim();
        } catch (error) {
          if (isAuthorizationError(error)) throw error;
          onError(error);
        }
      })();
    }
    await hydration;
    assertCurrent(ctx);
  }

  function persist(ctx) {
    trim();
    const snapshot = JSON.stringify({ schema: 1, entries: [...entries] });
    writes = writes
      .then(async () => {
        if (ctx.generation === generation && ctx.scope === getScope()) {
          await storage.setItem(keyFor(ctx.scope), snapshot);
        }
      })
      .catch(onError);
  }

  function load(kind, key, loader, ctx) {
    if (requests.has(key)) return requests.get(key);
    const request = Promise.resolve()
      .then(loader)
      .then((response) => {
        assertCurrent(ctx);
        const data = projectCatalogue(kind, response);
        entries.set(key, { savedAt: now(), data });
        retries.delete(key);
        persist(ctx);
        return data;
      })
      .catch((error) => {
        if (ctx.generation === generation && ctx.scope === getScope()) {
          if (isAuthorizationError(error)) {
            reset();
            // Do not restore rejected data from disk on the next request.
            writes = writes
              .then(() => storage.removeItem(keyFor(ctx.scope)))
              .catch(onError);
          } else {
            retries.set(key, now() + RETRY_AFTER);
          }
        }
        throw error;
      })
      .finally(() => {
        if (requests.get(key) === request) requests.delete(key);
      });
    requests.set(key, request);
    return request;
  }

  async function get(kind, ids, loader, { forceRefresh = false } = {}) {
    if (!FIELDS[kind]) throw new Error("Tipo de catálogo inválido");
    const ctx = context();
    await hydrate(ctx);
    const key = JSON.stringify([kind, ...ids]);
    const cached = entries.get(key);
    const age = cached ? now() - cached.savedAt : Infinity;
    if (!forceRefresh && age >= 0 && age < KEEP_FOR) {
      if (age >= FRESH_FOR && now() >= (retries.get(key) || 0)) {
        // Return the existing options immediately; refresh once in the background.
        load(kind, key, loader, ctx).catch(onError);
      }
      return copy(cached.data);
    }
    return copy(await load(kind, key, loader, ctx));
  }

  return { get, reset, flush: () => writes };
}

export {
  createCatalogueCache,
  projectCatalogue,
  isAuthorizationError,
  FRESH_FOR,
  KEEP_FOR,
};
